import React, { useEffect, useCallback, useState, lazy, Suspense, useMemo } from 'react';
import { DashboardLayout } from './components/layouts/DashboardLayout';
import { VerticalSelector } from './components/layouts/VerticalSelector';
import { StatsPanel } from './components/layouts/StatsPanel';
import { FilterPanel } from './components/filters/FilterPanel';
import { LabCard } from './components/cards/LabCard';
import { PracticeCard } from './components/cards/PracticeCard';
import { GeographicMap } from './components/visualizations/GeographicMap';
import { useFilterStore } from './store/filterStore';
import { pathologyLabs } from './data/pathologyData';
import { msoPractices } from './data/msoData';
import { applyPathologyFilters, applyMSOFilters } from './utils/filterLogic';
import { aggregatePracticeClusters } from './utils/clusteringUtils';
import { PathologyLab, MSOPractice } from './types';
import { motion, AnimatePresence } from 'framer-motion';
import { useParticleTransition } from './hooks/useParticleTransition';
import { isWebGLAvailable } from './utils/webglDetection';
import { ParticleTransition } from './components/animations/ParticleTransition';
import { PathSplitAnimation } from './components/animations/PathSplitAnimation';
import { VisualizationErrorBoundary } from './components/visualizations/VisualizationErrorBoundary';
import { verticalConfig } from './utils/verticalConfig';

// Lazy load particle components for better performance
const ParticleField = lazy(() => import('./components/visualizations/ParticleField'));
const ParticleFieldFallback = lazy(() => import('./components/visualizations/ParticleFieldFallback'));

function App() {
  const { vertical, activeFilters, setAnimating, setPipelineData, pipelineData } = useFilterStore();
  const [displayData, setDisplayData] = useState<(PathologyLab | MSOPractice)[]>([]);
  const { shouldShowParticles, isTransitioning } = useParticleTransition();
  const [hasWebGL] = useState(() => isWebGLAvailable());
  const [showPathSplit, setShowPathSplit] = useState(false);
  const [pathSplitType, setPathSplitType] = useState<'platform' | 'addon'>('platform');

  // Calculate clusters for MSO vertical (skip if MSO is disabled)
  const msoClusters = useMemo(() => {
    if (!verticalConfig.showMso) return [];
    return aggregatePracticeClusters(msoPractices);
  }, []);

  // Stable callbacks for animation completion (Fix 6: prevent re-render loops)
  const handleTransitionComplete = useCallback(() => {
    // Particle transition complete
  }, []);

  const handlePathSplitComplete = useCallback(() => {
    // Path split animation complete
  }, []);

  // Apply filters when they change
  useEffect(() => {
    if (activeFilters.length === 0) {
      // Show sample data when no filters
      setDisplayData(vertical === 'pathology' ? pathologyLabs.slice(0, 25) : msoPractices.slice(0, 25));
      setPipelineData([]);
      setShowPathSplit(false);
    } else {
      // Apply filters
      let filtered: (PathologyLab | MSOPractice)[];
      if (vertical === 'pathology') {
        filtered = applyPathologyFilters(pathologyLabs, activeFilters);

        // Detect strategy filter and trigger path split animation
        if (activeFilters.includes('strategy-platform')) {
          setPathSplitType('platform');
          setShowPathSplit(true);
        } else if (activeFilters.includes('strategy-addon')) {
          setPathSplitType('addon');
          setShowPathSplit(true);
        } else {
          setShowPathSplit(false);
        }
      } else {
        filtered = applyMSOFilters(msoPractices, activeFilters);
        setShowPathSplit(false);
      }
      setDisplayData(filtered.slice(0, 25)); // Show top 25 results
      setPipelineData(filtered);
      setAnimating(false);
    }
  }, [activeFilters, vertical, setPipelineData, setAnimating]);

  const Header = (
    <div className="flex justify-between items-center">
      {/* Left: Title */}
      <div>
        <h1 className="text-3xl font-bold">Deal Flow Intelligence Platform</h1>
        <p className="text-sm opacity-90 mt-1">
          Healthcare PE Investment Analysis Dashboard
        </p>
      </div>

      {/* Center: Logo */}
      <div className="flex-shrink-0">
        <a
          href="https://sightline.capital"
          target="_blank"
          rel="noopener noreferrer"
          className="block transition-opacity hover:opacity-80"
        >
          <img
            src="/sightline_logo_white_transparent.svg"
            alt="Sightline Capital"
            className="h-14"
          />
        </a>
      </div>

      {/* Right: Market Info */}
      <div className="text-right text-sm opacity-80">
        <div>Lower Middle Market</div>
        <div className="font-bold">$1.5M - $7M Revenue Focus</div>
      </div>
    </div>
  );

  return (
    <div className="App">
      <DashboardLayout
        header={Header}
        sidebar={<FilterPanel />}
        statsPanel={<StatsPanel />}
      >
        <div className="p-8">
          {/* Vertical Selector */}
          <div className="mb-12">
            <VerticalSelector />
          </div>

          {/* Particle Field for Pathology (when no filters) */}
          {shouldShowParticles && vertical === 'pathology' && (
            <VisualizationErrorBoundary fallbackMessage="3D particle visualization could not be loaded.">
              <Suspense fallback={<div className="text-center py-12">Loading visualization...</div>}>
                <div className="relative -mx-8 mt-4 mb-8">
                  {hasWebGL ? (
                    <ParticleField particleCount={5414} />
                  ) : (
                    <ParticleFieldFallback particleCount={5414} />
                  )}
                </div>
              </Suspense>
            </VisualizationErrorBoundary>
          )}

          {/* Particle Transition Animation */}
          {isTransitioning && vertical === 'pathology' && (
            <ParticleTransition
              isActive={isTransitioning}
              onTransitionComplete={handleTransitionComplete}
            />
          )}

          {/* Geographic Map for MSO (when no filters) */}
          {vertical === 'mso' && activeFilters.length === 0 && (
            <VisualizationErrorBoundary fallbackMessage="Map visualization could not be loaded.">
            <div className="relative -mx-8 mt-4 mb-8">
              <div className="h-[700px] w-full">
                <GeographicMap
                  clusters={msoClusters.map(cluster => ({
                    id: cluster.id,
                    name: cluster.name,
                    center: cluster.center,
                    practiceCount: cluster.practiceCount,
                  }))}
                />
              </div>

              {/* Map overlay text */}
              <div className="absolute top-8 left-1/2 transform -translate-x-1/2 text-center pointer-events-none">
                <div className="text-6xl font-bold text-white drop-shadow-lg">
                  {msoPractices.length.toLocaleString()}
                </div>
                <div className="text-xl text-white opacity-80 mt-2">
                  Medical Practices → 76 MSO Targets Across 9 Markets
                </div>
                <div className="text-sm text-white opacity-60 mt-4">
                  Click a market to explore practices in that region
                </div>
              </div>
            </div>
            </VisualizationErrorBoundary>
          )}

          {/* Welcome Message (pathology fallback when particles unavailable) */}
          {activeFilters.length === 0 && !shouldShowParticles && vertical === 'pathology' && (
            <div className="text-center py-12">
              <h2 className="text-2xl font-bold text-white mb-4">
                5,414 Anatomical Pathology Laboratories
              </h2>
              <p className="text-light-gray mb-6">
                Select filters on the left and click Apply to begin the pipeline analysis
              </p>
              <div className="text-sm text-medium-gray">
                Showing sample of {displayData.length} labs
              </div>
            </div>
          )}

          {/* Path Split Animation - Only for strategy filters */}
          {showPathSplit && vertical === 'pathology' && !shouldShowParticles && (
            <PathSplitAnimation
              labs={pathologyLabs}
              pathType={pathSplitType}
              isActive={showPathSplit}
              onComplete={handlePathSplitComplete}
            />
          )}

          {/* Results */}
          {activeFilters.length > 0 && !showPathSplit && (
            <div className="mb-6">
              <h2 className="text-xl font-bold text-white mb-2">
                Filter Results: {pipelineData.length} {vertical === 'pathology' ? 'Labs' : 'Practices'}
              </h2>
              <p className="text-sm text-light-gray">
                Showing top {displayData.length} of {pipelineData.length} results
              </p>
            </div>
          )}

          {/* Card Grid - Only show when NOT showing particles and NOT showing path split */}
          {!shouldShowParticles && !showPathSplit && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <AnimatePresence mode="popLayout">
                {displayData.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ duration: 0.3, delay: index * 0.02 }}
                  >
                    {vertical === 'pathology' ? (
                      <LabCard lab={item as PathologyLab} variant="default" />
                    ) : (
                      <PracticeCard practice={item as MSOPractice} variant="default" />
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </div>
      </DashboardLayout>
    </div>
  );
}

export default App;
